/**
 * Resolves @sakhtar/design-system's `--sk-*` custom properties to real
 * color strings for ECharts' canvas renderer (canvas paints can't reference
 * `var(--sk-*)` the way DOM/CSS can — they need the resolved value).
 * `getComputedStyle` on the chart's own container node picks up whichever
 * `[data-theme]`/`[data-brand]` ancestor is actually in effect (light/dark,
 * tenant brand), rather than assuming `document.documentElement`, because
 * ThemeProvider stamps those attributes on its own wrapper div, not <html>
 * (see packages/design-system/src/react/theme/ThemeProvider.tsx).
 *
 * `categorical` is `--sk-chart-{1,6,3,2,4,5}` in that exact order — the
 * fixed, validated order from packages/design-system's
 * `v2ChartSeriesOrder` (tokens/v2/colors.ts). Assign by position, never
 * re-sorted — see that file's comment for why (adjacent-hue CVD floor).
 */
const CATEGORICAL_VARS = [
  '--sk-chart-1',
  '--sk-chart-6',
  '--sk-chart-3',
  '--sk-chart-2',
  '--sk-chart-4',
  '--sk-chart-5',
] as const;

export const CHART_FONT_FALLBACK = '"Peyda", system-ui, sans-serif';

export interface ChartTheme {
  readonly categorical: readonly string[];
  readonly text: string;
  readonly textMuted: string;
  readonly grid: string;
  readonly surface: string;
  readonly success: string;
  readonly warn: string;
  readonly danger: string;
  /** Resolved `--sk-font` (Peyda first): canvas text can't use `var()` or inherit CSS fonts. */
  readonly fontFamily: string;
}

let colorProbe: CanvasRenderingContext2D | null | undefined;

/**
 * ECharts (zrender) only parses hex / rgb() / hsl(). The design tokens are
 * `oklch(...)`, which a canvas paints fine but ECharts cannot parse: when a
 * datum is hovered ECharts derives its emphasis colour from that string, gets
 * garbage, and the hovered bar / slice / dot is not drawn at all. So every
 * colour is read back through a canvas pixel and handed over as plain sRGB.
 * Unparseable input and non-DOM environments return the value unchanged.
 */
export function toRgbColor(value: string): string {
  if (!value || typeof document === 'undefined') return value;
  if (colorProbe === undefined) {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    colorProbe = canvas.getContext('2d', { willReadFrequently: true });
  }
  const ctx = colorProbe;
  if (!ctx) return value;
  const sentinel = '#010203';
  ctx.fillStyle = sentinel;
  ctx.fillStyle = value; // an invalid colour is ignored and leaves the sentinel
  if (ctx.fillStyle === sentinel && value.replace(/\s/g, '').toLowerCase() !== sentinel) {
    return value;
  }
  ctx.clearRect(0, 0, 1, 1);
  ctx.fillRect(0, 0, 1, 1);
  const [r = 0, g = 0, b = 0, a = 255] = ctx.getImageData(0, 0, 1, 1).data;
  return a === 255
    ? `rgb(${r}, ${g}, ${b})`
    : `rgba(${r}, ${g}, ${b}, ${Number((a / 255).toFixed(3))})`;
}

export function resolveChartTheme(container: HTMLElement): ChartTheme {
  const style = getComputedStyle(container);
  const read = (name: string) => style.getPropertyValue(name).trim();
  const color = (name: string) => toRgbColor(read(name));
  return {
    categorical: CATEGORICAL_VARS.map(color),
    text: color('--sk-fg'),
    textMuted: color('--sk-fg-muted'),
    grid: color('--sk-divider'),
    surface: color('--sk-bg-elev'),
    success: color('--sk-success'),
    warn: color('--sk-warn'),
    danger: color('--sk-danger'),
    fontFamily: read('--sk-font') || CHART_FONT_FALLBACK,
  };
}
